import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Calendar, Clock, MapPin, Users, CheckCircle2,
  Laptop, FileQuestion, Palette, Sparkles,
  Code, Layout, Zap, Rocket, X,
  ArrowRight, Shield, TrendingUp, Star, Play, Pause } from
"lucide-react";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./components/ui/accordion";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.15 } },
  viewport: { once: true }
};

const staggerItem = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

// Logo Component
const Logo = "https://customer-assets.emergentagent.com/job_b7146a69-59d4-4e5f-9697-2f019e84fd69/artifacts/11kz9ncp_SM%20Logo%20dark%20text%20%282%29.png";

// Space Elements Component
const SpaceElements = () =>
<>
    {/* Nebula glows */}
    <div className="nebula-glow" style={{ width: '600px', height: '600px', top: '-200px', right: '-100px', background: 'radial-gradient(circle, rgba(124,58,237,0.3) 0%, transparent 70%)' }} />
    <div className="nebula-glow" style={{ width: '400px', height: '400px', bottom: '100px', left: '-100px', background: 'radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)', animationDelay: '-5s' }} />
    <div className="nebula-glow" style={{ width: '300px', height: '300px', top: '50%', right: '10%', background: 'radial-gradient(circle, rgba(236,72,153,0.2) 0%, transparent 70%)', animationDelay: '-3s' }} />
    
    {/* Orbits */}
    <div className="orbit" style={{ width: '800px', height: '800px', top: '-300px', right: '-200px', animationDuration: '40s' }} />
    <div className="orbit" style={{ width: '600px', height: '600px', bottom: '-200px', left: '-150px', animationDuration: '30s', animationDirection: 'reverse' }} />
    <div className="orbit" style={{ width: '400px', height: '400px', top: '20%', left: '10%', animationDuration: '25s' }} />
    
    {/* Planets */}
    <div className="planet floating" style={{ width: '12px', height: '12px', top: '15%', right: '20%', background: 'linear-gradient(135deg, #7c3aed, #ec4899)' }} />
    <div className="planet floating-delayed" style={{ width: '8px', height: '8px', top: '40%', left: '15%', background: 'linear-gradient(135deg, #3b82f6, #7c3aed)' }} />
    <div className="planet floating" style={{ width: '6px', height: '6px', bottom: '30%', right: '25%', background: 'linear-gradient(135deg, #ec4899, #3b82f6)', animationDelay: '-2s' }} />
    
  </>;


// Stars Background Component
const StarsBackground = ({ count = 50 }) => {
  const stars = Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: Math.random() * 2 + 1,
    delay: Math.random() * 3,
    duration: Math.random() * 2 + 2
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((star) =>
      <motion.div
        key={star.id}
        className="absolute rounded-full bg-white"
        style={{
          left: star.left,
          top: star.top,
          width: star.size,
          height: star.size
        }}
        animate={{
          opacity: [0.3, 1, 0.3],
          scale: [1, 1.2, 1]
        }}
        transition={{
          duration: star.duration,
          delay: star.delay,
          repeat: Infinity,
          ease: "easeInOut"
        }} />

      )}
    </div>);

};

// Form Modal Component
const FormModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      // Load JotForm embed handler script
      const script = document.createElement('script');
      script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
      script.async = true;
      script.onload = () => {
        if (window.jotformEmbedHandler) {
          window.jotformEmbedHandler("iframe[id='JotFormIFrame-260771524073859']", "https://form.jotform.com/");
        }
      };
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="registration-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Secure Your Seat</DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          <iframe
            id="JotFormIFrame-260771524073859"
            title="AI Website Workshop Form"
            allowTransparency="true"
            allow="geolocation; microphone; camera; fullscreen; payment"
            src="https://form.jotform.com/260771524073859"
            frameBorder="0"
            style={{ minWidth: '100%', maxWidth: '100%', height: '750px', border: 'none' }}
            scrolling="no" />

        </div>
      </DialogContent>
    </Dialog>);

};

// Contact Modal Component (Placeholder for JotForm)
const ContactModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
      script.async = true;
      script.onload = () => {
        if (window.jotformEmbedHandler) {
          window.jotformEmbedHandler("iframe[id='JotFormIFrame-260812130875454']", "https://form.jotform.com/");
        }
      };
      document.body.appendChild(script);
      return () => document.body.removeChild(script);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="contact-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Get in Touch</DialogTitle>
          <DialogDescription className="text-slate-500">
            Have questions or think your business might be a good fit? Let us know.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          <iframe
            id="JotFormIFrame-260812130875454"
            title="AI Website Workshop Information Form"
            allowTransparency="true"
            allow="geolocation; microphone; camera; fullscreen; payment"
            src="https://form.jotform.com/260812130875454"
            frameBorder="0"
            style={{ minWidth: '100%', maxWidth: '100%', height: '700px', border: 'none' }}
            scrolling="no"
          />
        </div>
      </DialogContent>
    </Dialog>);

};

// Navigation Component
const Navigation = ({ onCtaClick }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      scrolled ?
      'bg-white/90 backdrop-blur-xl border-b border-violet-100 shadow-lg shadow-violet-500/5' :
      'bg-transparent'}`
      }
      data-testid="navigation">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <motion.img
            src={Logo}
            alt="Spaceship Media"
            className="h-12 md:h-14"
            data-testid="nav-logo"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }} />

          <div className="hidden md:flex items-center gap-3">
            <span className="text-xs text-slate-500 font-medium tracking-wide" data-testid="nav-date-text">
              Next Workshop: 28 May • 1:00 PM
            </span>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={onCtaClick}
                className="bg-gradient-to-r from-violet-600 to-blue-500 hover:from-violet-700 hover:to-blue-600 text-white rounded-full px-6 py-2 text-sm font-medium shadow-lg shadow-violet-500/25"
                data-testid="nav-cta-button">
                Secure Your Seat
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.nav>);

};

// Hero Section
const HeroSection = ({ onCtaClick }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-blue-50 to-pink-50" />
      
      {/* Animated background elements */}
      <div className="hero-glow" />
      
      {/* Space elements */}
      <motion.div style={{ opacity }} className="absolute inset-0 pointer-events-none">
        <SpaceElements />
      </motion.div>
      
      {/* Animated gradient orbs */}
      <motion.div
        style={{ y }}
        className="absolute top-20 right-10 w-96 h-96 rounded-full bg-gradient-to-br from-violet-300/30 to-transparent blur-3xl" />

      <motion.div
        style={{ y: useTransform(scrollY, [0, 500], [0, 100]) }}
        className="absolute bottom-40 left-10 w-72 h-72 rounded-full bg-gradient-to-br from-blue-300/30 to-transparent blur-3xl" />

      <motion.div
        style={{ y: useTransform(scrollY, [0, 500], [0, 80]) }}
        className="absolute top-40 left-1/4 w-48 h-48 rounded-full bg-gradient-to-br from-pink-300/30 to-transparent blur-3xl" />


      {/* Floating rocket */}
      <motion.div
        className="absolute top-32 right-20 hidden lg:block"
        animate={{ y: [0, -20, 0], rotate: [-45, -40, -45] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>

        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-xl shadow-violet-500/30">
          <Rocket className="w-8 h-8 text-white" />
        </div>
      </motion.div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 relative">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl">

          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="inline-flex items-center gap-2 text-sm font-medium text-violet-600 mb-6 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-violet-200 shadow-lg shadow-violet-500/10">

            <span className="text-base">🇦🇺</span>
            Australia's First AI Website Workshop • Melbourne • Limited Seats
            <span className="flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-violet-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-500"></span>
            </span>
          </motion.span>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] text-slate-900 mb-6">
            Build a Professional Website in One Afternoon{' '}
            <span className="gradient-text text-glow">Using AI</span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-slate-600 mb-4 font-medium">

            No Code. No WordPress.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-lg md:text-xl text-slate-500 leading-relaxed max-w-2xl mb-4">

            A 4-hour, hands-on workshop where you'll build a professional, 
            conversion-ready website from scratch — with SEO built in from day one — guided step-by-step.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="text-lg md:text-xl text-slate-600 font-medium max-w-2xl mb-10">
            Built for business owners ready to take control of their online presence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col gap-3">

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={onCtaClick}
                className="bg-gradient-to-r from-violet-600 via-blue-500 to-pink-500 hover:from-violet-700 hover:via-blue-600 hover:to-pink-600 text-white rounded-full px-10 py-6 text-lg font-medium shadow-xl shadow-violet-500/25 group"
                data-testid="hero-cta-button">

                Secure Your Seat — <span className="line-through opacity-60">$3k</span> $749
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
            <p className="text-sm text-slate-400 ml-1">
              Be part of the first group in Australia to learn this.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>);

};

// Problem Section
const ProblemSection = () =>
<section className="relative py-24 md:py-32 gradient-section-purple overflow-hidden" id="problem" data-testid="problem-section">
    {/* Decorative elements */}
    <div className="absolute top-10 right-10 w-32 h-32 rounded-full bg-violet-200/50 blur-2xl" />
    <div className="absolute bottom-10 left-10 w-24 h-24 rounded-full bg-pink-200/50 blur-2xl" />
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div {...fadeInUp} className="max-w-3xl">
        <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-violet-100 px-4 py-2 rounded-full">
          <Zap className="w-4 h-4" />
          The Problem
        </span>
        <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-8">
          Trust us — <span className="gradient-text">we know.</span>
        </h2>
      </motion.div>
      
      <motion.div {...staggerContainer} className="grid md:grid-cols-2 gap-12 mt-12">
        <motion.div {...staggerItem} className="space-y-6">
          <p className="text-lg text-slate-600 leading-relaxed">
            For years businesses have been paying{' '}
            <span className="font-semibold text-slate-900 bg-violet-100 px-2 py-0.5 rounded">$3k–$10k+ for websites</span>{' '}
            that are little more than: a template, a few plugins, a page builder, 
            and a logo slapped on top.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            Six months later something breaks... A plugin stops working. 
            WordPress needs updating. Then comes the email:
          </p>
          <motion.div
          className="bg-white rounded-2xl p-6 border-l-4 border-gradient-to-b from-violet-500 to-pink-500 shadow-lg"
          whileHover={{ x: 5 }}>

            <p className="text-lg text-slate-700 italic">
              "We can fix it — here's <span className="font-bold text-violet-600">another invoice</span>."
            </p>
          </motion.div>
        </motion.div>
        
        <motion.div {...staggerItem} className="space-y-6">
          <p className="text-lg text-slate-600 leading-relaxed">
            This is how the industry has operated for a long time.
          </p>
          <p className="text-xl font-medium text-slate-900 leading-relaxed bg-gradient-to-r from-violet-50 to-pink-50 p-6 rounded-2xl border border-violet-100">Now with AI, you can create your own website at a fraction of the cost...

        </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            Instead of relying on agencies at all —{' '}
            <span className="font-semibold text-slate-900">
              we can show you exactly how websites are built.
            </span>
          </p>
        </motion.div>
      </motion.div>
    </div>
  </section>;


// Philosophy Section (Dark Space Theme)
const PhilosophySection = () => {
  const founders = [
  {
    name: "Hamza Choudhary",
    role: "Co-Founder & Head of Technology",
    badge: "AI Website Workshop Instructor",
    experience: "12+ Years",
    expertise: "Website Design & Development",
    image: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/a5jy6pme_Hamza-Choudhary-Spaceship-Media-Director%20%281%29.jpg",
    isPrimary: true,
    description: "Hamza leads all technology initiatives at Spaceship Media. With over a decade of experience in web design and development, he's built websites for countless businesses across Australia."
  },
  {
    name: "Ish Naeem",
    role: "Co-Founder & Head of Digital Marketing",
    badge: "Marketing Workshop Instructor",
    experience: "12+ Years",
    expertise: "SEO, Meta & Google Ads",
    image: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/j7gl6qoi_Ish-Naeem.jpg",
    isPrimary: false,
    description: "Ish drives the marketing strategy at Spaceship Media. His expertise in SEO, Google Ads, and Meta advertising has helped businesses achieve measurable growth."
  }];


  return (
    <section className="relative py-32 md:py-40 space-bg overflow-hidden" id="philosophy" data-testid="philosophy-section">
      <StarsBackground count={80} />
      
      {/* Nebula effects */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-violet-600/20 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-600/20 rounded-full blur-[100px]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-pink-600/10 rounded-full blur-[80px]" />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div {...fadeInUp} className="text-center mb-16">
          <motion.div
            className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-300 mb-4 bg-violet-500/20 backdrop-blur-sm px-4 py-2 rounded-full border border-violet-500/30"
            animate={{ boxShadow: ['0 0 20px rgba(139,92,246,0.3)', '0 0 40px rgba(139,92,246,0.5)', '0 0 20px rgba(139,92,246,0.3)'] }}
            transition={{ duration: 2, repeat: Infinity }}>

            <Star className="w-4 h-4" />
            Our Philosophy
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-8">
            We believe businesses deserve <span className="gradient-text">better.</span>
          </h2>
          
          <div className="text-lg md:text-xl text-slate-300 leading-relaxed space-y-6 max-w-2xl mx-auto">
            <p>We started Spaceship Media years ago with a simple belief — businesses deserve better.

            </p>
            <p className="text-slate-400">
              Better than templates.<br />
              Better than constant fixes.<br />
              Better than relying on agencies for every change.
            </p>
            <p>
              We focus on giving you <span className="text-white font-medium">control</span>, <span className="text-white font-medium">simplicity</span>, and <span className="text-white font-medium">independence</span>.
            </p>
          </div>
        </motion.div>

        {/* Meet our Founders */}
        <motion.div {...fadeInUp} className="mt-20">
          <h3 className="text-2xl md:text-3xl font-semibold text-white text-center mb-12">
            Meet our Founders
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            {founders.map((founder, i) =>
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              whileHover={{ y: -8 }}
              className="relative bg-white rounded-3xl p-8 shadow-xl">

                {/* Badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold text-white px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap ${founder.isPrimary ? 'bg-gradient-to-r from-violet-600 to-pink-500' : 'bg-gradient-to-r from-blue-500 to-cyan-500'}`}>
                    {founder.isPrimary && <Star className="w-3 h-3" />}
                    {founder.badge}
                  </span>
                </div>
                
                <div className="flex flex-col items-center text-center">
                  {/* Image with ring */}
                  <motion.div
                  className="w-40 h-40 rounded-full overflow-hidden mb-6 ring-4 ring-violet-200 ring-offset-4"
                  whileHover={{ scale: 1.05 }}>

                    <img
                    src={founder.image}
                    alt={founder.name}
                    className="w-full h-full object-cover object-top" />

                  </motion.div>
                  
                  <h4 className="text-2xl font-bold text-slate-900 mb-1">{founder.name}</h4>
                  <p className="text-violet-600 font-medium mb-4">{founder.role}</p>
                  
                  {/* Experience tags */}
                  <div className="flex flex-wrap justify-center gap-3 mb-4">
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700 bg-slate-100 px-3 py-1.5 rounded-full">
                      <TrendingUp className="w-4 h-4 text-violet-500" />
                      {founder.experience}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full">
                      {founder.expertise}
                    </span>
                  </div>
                  
                  <p className="text-slate-500 text-base leading-relaxed">
                    {founder.description}
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </section>);

};


// Outcome Section
const OutcomeSection = () =>
<section className="relative py-24 md:py-32 bg-white overflow-hidden" id="outcome" data-testid="outcome-section">
    {/* Decorative orbits */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-1/4 left-1/4 w-64 h-64 border border-violet-200/30 rounded-full" />
      <div className="absolute bottom-1/4 right-1/4 w-48 h-48 border border-pink-200/30 rounded-full" />
    </div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div
      {...scaleIn}
      className="text-center py-12">

        <motion.span
        className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-blue-600 mb-6 bg-blue-100 px-4 py-2 rounded-full"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 2, repeat: Infinity }}>

          <Rocket className="w-4 h-4" />
          The Outcome
        </motion.span>
        <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 mb-8">
          You'll have a{' '}
          <span className="gradient-text text-glow">live website.</span>
        </h2>
        <motion.blockquote
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="max-w-3xl mx-auto bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-blue-100">

          <p className="text-xl md:text-2xl text-slate-600 leading-relaxed italic">
            "The website you build in this workshop will be{' '}
            <span className="font-bold text-slate-900 not-italic bg-gradient-to-r from-violet-100 to-pink-100 px-2 py-1 rounded">
              better than what many agencies charge thousands for.
            </span>"
          </p>
        </motion.blockquote>
      </motion.div>
    </div>
  </section>;


// Social Proof Section - Video Showcase
const SocialProofSection = () => {
  const [playingIndex, setPlayingIndex] = useState(null);
  const videoRefs = useRef([]);

  const websites = [
  {
    name: "Securelec",
    video: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/vawlpeof_Securelec.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/24r8zq7f_securelec%20video%20thumbnail.png",
    gradient: "from-blue-600 via-blue-800 to-slate-900"
  },
  {
    name: "Maple",
    video: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/20dzfc81_Maple.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/7fctqi9z_Maple%20video%20thumbnail.png",
    gradient: "from-emerald-600 via-teal-800 to-slate-900"
  },
  {
    name: "Hearts Dental",
    video: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/eq9ity0g_HeartsDental.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/tt6s63r3_Hearts%20Dental%20video%20thumbnail.png",
    gradient: "from-pink-600 via-rose-800 to-slate-900"
  },
  {
    name: "TMG",
    video: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/4sh7r7d8_TMG.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_spaceship-workshop/artifacts/op11moxk_TMG%20video%20thumbnail.png",
    gradient: "from-violet-600 via-purple-800 to-slate-900"
  }];


  const handlePlayClick = (index) => {
    if (playingIndex === index) {
      // Pause if already playing
      videoRefs.current[index]?.pause();
      setPlayingIndex(null);
    } else {
      // Pause any currently playing video
      if (playingIndex !== null && videoRefs.current[playingIndex]) {
        videoRefs.current[playingIndex].pause();
      }
      // Play the clicked video
      videoRefs.current[index]?.play();
      setPlayingIndex(index);
    }
  };

  const handleVideoEnd = (index) => {
    if (playingIndex === index) {
      setPlayingIndex(null);
    }
  };

  return (
    <section className="relative py-28 md:py-36 bg-slate-50 overflow-hidden" id="social-proof" data-testid="social-proof-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="mb-14 text-center">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-violet-100 px-4 py-2 rounded-full">
            <CheckCircle2 className="w-4 h-4" />
            Real Results
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
            Websites Built Using <span className="gradient-text">This System</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Real websites created using the exact AI workflows you'll learn in this workshop.
          </p>
        </motion.div>

        {/* Video Showcase - Horizontal Scroll */}
        <div className="relative">
          <div
            className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory scroll-smooth"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}>

            <style>{`
              .video-scroll-container::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {websites.map((site, i) =>
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex-shrink-0 snap-start first:pl-0"
              style={{ width: 'calc(50% - 12px)', minWidth: '340px', maxWidth: '580px' }}>

                <motion.div
                className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 group cursor-pointer"
                whileHover={{ y: -8 }}
                onClick={() => handlePlayClick(i)}>

                  {/* Video Container */}
                  <div className={`relative aspect-[16/10] overflow-hidden`}>
                    {/* Gradient background as fallback */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${site.gradient} flex items-center justify-center`}>
                      <div className="text-center">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
                          <Layout className="w-6 h-6 text-white/80" />
                        </div>
                        <span className="text-white/60 text-sm font-medium">{site.name}</span>
                      </div>
                    </div>
                    
                    {/* Mobile: Show thumbnail image / Desktop: Show video with poster */}
                    {/* Thumbnail shown when not playing */}
                    {playingIndex !== i &&
                  <img
                    src={site.thumbnail}
                    alt={site.name}
                    className="absolute inset-0 w-full h-full object-cover" />
                  }
                    
                    {/* Video element for both mobile and desktop */}
                    <video
                    ref={(el) => {
                      videoRefs.current[i] = el;
                    }}
                    src={site.video}
                    poster={site.thumbnail}
                    className={`absolute inset-0 w-full h-full object-cover ${playingIndex === i ? 'z-10' : ''}`}
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      e.target.currentTime = 0.5;
                    }}
                    onEnded={() => handleVideoEnd(i)} />

                    
                    {/* Play/Pause Overlay */}
                    <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                  playingIndex === i ?
                  'bg-black/0 opacity-0 hover:opacity-100 hover:bg-black/20' :
                  'bg-black/30 group-hover:bg-black/40'}`
                  }>
                      <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-2xl transition-all duration-300 ${
                      playingIndex === i ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`
                      }>

                        {playingIndex === i ?
                      <Pause className="w-7 h-7 md:w-8 md:h-8 text-slate-900" /> :

                      <Play className="w-7 h-7 md:w-8 md:h-8 text-slate-900 ml-1" />
                      }
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </div>
          
          {/* Scroll Indicator Fade */}
          <div className="absolute right-0 top-0 bottom-6 w-24 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none hidden md:block" />
        </div>

        {/* Tagline */}
        <motion.p
          {...fadeInUp}
          className="text-center mt-12 text-lg text-slate-500">

          <span className="font-medium text-slate-700">These websites were built in hours</span> — not weeks.
        </motion.p>
      </div>
    </section>);

};

// Authority Strip
const AuthorityStrip = () => {
  const stats = [
  { icon: TrendingUp, value: "12+ Years", label: "Building websites and digital systems for real businesses" },
  { icon: Code, value: "Real Workflows", label: "The exact techniques used for paying clients" },
  { icon: Layout, value: "Practical Focus", label: "Hands-on methods that actually work — no fluff" }];


  return (
    <div className="relative py-16 bg-gradient-to-r from-violet-600 via-blue-600 to-pink-600 overflow-hidden" data-testid="authority-strip">
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjwvZz48L2c+PC9zdmc+')] animate-pulse" />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div {...staggerContainer} className="grid md:grid-cols-3 gap-8 md:gap-12">
          {stats.map((stat, i) =>
          <motion.div
            key={i}
            {...staggerItem}
            className="flex items-start gap-4 text-white">

              <motion.div
              className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0"
              whileHover={{ scale: 1.1, rotate: 5 }}>

                <stat.icon className="w-7 h-7 text-white" />
              </motion.div>
              <div>
                <h3 className="text-2xl font-bold mb-1">{stat.value}</h3>
                <p className="text-white/80">{stat.label}</p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>);

};

// Who It's For Section
const WhoItsForSection = ({ onContactClick }) => {
  const businessTypes = [
  "Consultants, business coaches, and HR professionals",
  "Trainers, coaches, and sales trainers",
  "Personal trainers, yoga and pilates instructors",
  "Private educators",
  "Photographers and solo videographers", "Tattoo artists, and henna artists",

  "Stylists and interior designers",
  "Naturopaths, chiropractors, psychologists",
  "Nutritionists, physios, hypnotherapists",
  "Massage therapists and remedial massage practitioners",
  "Cleaners and handymen",
  "Pest control services",
  "Locksmiths",
  "Window cleaners",
  "Property and site inspectors",
  "Brow and nail technicians",
  "Window tinting and ceramic coating services",
  "Cafes, restaurants, and small caterers",
  "Not-for-profits and fundraising organisations",
  "Printers"];


  return (
    <section className="relative py-24 md:py-32 gradient-section-light overflow-hidden" id="who-its-for" data-testid="who-its-for-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-violet-100 px-4 py-2 rounded-full">
            <Users className="w-4 h-4" />
            Perfect For You
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
            Who This Workshop Is <span className="gradient-text">For</span>
          </h2>
          <p className="text-lg text-slate-500">
            Solopreneurs, startups, and small business owners.
          </p>
        </motion.div>

        {/* Business Types Grid */}
        <motion.div
          {...fadeInUp}
          className="bg-white rounded-3xl border border-slate-200 p-8 md:p-10 shadow-lg mb-8">

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {businessTypes.map((type, i) =>
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.02 }}
              className="flex items-center gap-2 py-2">

                <span className="w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-slate-600">{type}</span>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Highlight Line */}
        <motion.p
          {...fadeInUp}
          className="text-center text-lg font-medium text-slate-700 mb-8">

          If you offer a service or run a small business, this workshop is likely for you.
        </motion.p>

        {/* eCommerce Note */}
        <motion.div
          {...fadeInUp}
          className="bg-slate-50 rounded-2xl p-6 border border-slate-200 max-w-3xl mx-auto">

          <p className="text-slate-500 text-sm text-center">
            <span className="font-semibold text-slate-700">Note:</span> This workshop is <span className="font-semibold text-slate-900">not designed for eCommerce businesses</span>. A dedicated workshop for eCommerce is coming soon. If you'd like to be notified or think your business may still be a good fit, feel free to{' '}
            <button
              onClick={onContactClick}
              className="font-semibold text-violet-600 hover:text-violet-700 underline underline-offset-2 cursor-pointer">

              reach out to us
            </button>.
          </p>
        </motion.div>
      </div>
    </section>);

};

// Marquee Banner
const MarqueeBanner = () => {
  const messages = [
  "Build modern websites using AI — without code",
  "Australia's first AI website workshop",
  "Build a live website in just one afternoon"];


  return (
    <div className="relative py-8 bg-gradient-to-r from-slate-900 via-violet-900 to-slate-900 overflow-hidden" data-testid="marquee-banner">
      <StarsBackground count={30} />
      <div className="marquee-track">
        {[...Array(6)].map((_, i) =>
        <span key={i} className="text-xl md:text-2xl font-semibold text-white/90 whitespace-nowrap flex items-center">
            {messages.map((msg, j) =>
          <span key={j} className="flex items-center">
                <span className="mx-8">{msg}</span>
                <span className="text-violet-400 mx-2">•</span>
              </span>
          )}
          </span>
        )}
      </div>
    </div>);

};


// Curriculum Section
const CurriculumSection = () => {
  const modules = [
  {
    num: "01",
    icon: Layout,
    title: "Website Foundations",
    duration: "45 mins",
    color: "violet",
    items: ["What makes a website feel premium and trustworthy", "Layout, hierarchy, spacing, and flow", "Why most DIY websites look amateur — and how to avoid it"]
  },
  {
    num: "02",
    icon: Code,
    title: "Build With AI",
    duration: "90 mins",
    color: "blue",
    items: ["Turning ideas into complete pages using AI", "Structuring homepage, services, and lead-generating CTAs", "Building your website live during the workshop"]
  },
  {
    num: "03",
    icon: Sparkles,
    title: "Design Polish & Motion",
    duration: "45 mins",
    color: "pink",
    items: ["Subtle animations that elevate credibility", "Interaction design without breaking usability", "Making your website feel agency-level"]
  },
  {
    num: "04",
    icon: Rocket,
    title: "Launch & Reuse",
    duration: "30 mins",
    color: "violet",
    items: ["Publish your website live", "Learn how to reuse this system again and again", "How to improve conversions and turn visitors into enquiries"]
  }];


  const colorClasses = {
    violet: { bg: 'from-violet-500 to-purple-600', num: 'text-violet-200' },
    blue: { bg: 'from-blue-500 to-cyan-600', num: 'text-blue-200' },
    pink: { bg: 'from-pink-500 to-rose-600', num: 'text-pink-200' }
  };

  return (
    <section className="relative py-24 md:py-32 gradient-section-purple overflow-hidden" id="curriculum" data-testid="curriculum-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-violet-200">
            <Sparkles className="w-4 h-4" />
            What You'll Learn
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
            Workshop <span className="gradient-text">Curriculum</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            A structured, hands-on journey from idea to a live website.
          </p>
        </motion.div>

        <motion.div {...staggerContainer} className="grid md:grid-cols-2 gap-6">
          {modules.map((mod, i) => {
            const colors = colorClasses[mod.color];
            return (
              <motion.div
                key={i}
                {...staggerItem}
                whileHover={{ y: -8 }}
                className="bg-white rounded-3xl border border-violet-100 p-8 shadow-lg hover:shadow-xl transition-all duration-300 group">

                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <span className={`text-6xl font-bold ${colors.num} group-hover:text-violet-300 transition-colors`}>
                      {mod.num}
                    </span>
                    <motion.div
                      className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.bg} flex items-center justify-center shadow-lg`}
                      whileHover={{ rotate: 10, scale: 1.1 }}>

                      <mod.icon className="w-7 h-7 text-white" />
                    </motion.div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-400 bg-slate-50 px-3 py-1.5 rounded-full">
                    <Clock className="w-3.5 h-3.5" />
                    {mod.duration}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4">{mod.title}</h3>
                <ul className="space-y-3">
                  {mod.items.map((item, j) =>
                  <li key={j} className="flex items-start gap-3 text-slate-500">
                      <CheckCircle2 className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  )}
                </ul>
              </motion.div>);

          })}
        </motion.div>
      </div>
    </section>);

};

// What to Bring Section
const WhatToBringSection = () => {
  const items = [
  { icon: Laptop, title: "Laptop", desc: "Mac or Windows — anything you're comfortable using.", color: "violet" },
  { icon: Sparkles, title: "AI Builder Subscription", desc: "An AI builder plan (~USD $20/month). Don't worry — we'll guide you through this on the day.", color: "blue" },
  { icon: FileQuestion, title: "Pre-Workshop Questionnaire", desc: "You'll receive a short questionnaire after registering. Completing this helps you get the most out of the session.", color: "pink" },
  { icon: Palette, title: "Your Logo", desc: "If you already have a logo, bring it along. If not, just have a colour direction in mind.", color: "violet" }];


  const colorClasses = {
    violet: 'from-violet-500 to-purple-600',
    blue: 'from-blue-500 to-cyan-600',
    pink: 'from-pink-500 to-rose-600'
  };

  return (
    <section className="relative py-24 md:py-32 bg-slate-50 overflow-hidden" id="what-to-bring" data-testid="what-to-bring-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="mb-12">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-violet-100 px-4 py-2 rounded-full">
            <CheckCircle2 className="w-4 h-4" />
            Be Prepared
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
            What You'll Need to <span className="gradient-text">Bring</span>
          </h2>
          <p className="text-lg text-slate-500">
            Just a few simple things — we'll handle the rest.
          </p>
        </motion.div>

        <motion.div {...staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((item, i) =>
          <motion.div
            key={i}
            {...staggerItem}
            whileHover={{ y: -10, scale: 1.02 }}
            className="bg-white rounded-3xl border border-slate-200 p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center">

              <motion.div
              className={`w-18 h-18 rounded-2xl bg-gradient-to-br ${colorClasses[item.color]} flex items-center justify-center mx-auto mb-5 shadow-lg w-16 h-16`}
              whileHover={{ rotate: 10 }}>

                <item.icon className="w-8 h-8 text-white" />
              </motion.div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-sm text-slate-500">{item.desc}</p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// Workshop Details Section
const WorkshopDetailsSection = () => {
  const details = [
  { icon: Calendar, label: "Date", value: "28 May 2026", color: "violet" },
  { icon: Clock, label: "Time", value: "1:00 PM", color: "blue" },
  { icon: Clock, label: "Duration", value: "4 hours", color: "pink" },
  { icon: MapPin, label: "Location", value: "Spaceship Media Office in Melbourne", color: "pink" },
  { icon: Users, label: "Format", value: "Small Group (15 Seats Only)", color: "violet" }];


  const colorClasses = {
    violet: 'from-violet-500 to-purple-600',
    blue: 'from-blue-500 to-cyan-600',
    pink: 'from-pink-500 to-rose-600'
  };

  return (
    <section className="relative py-24 md:py-32 bg-white overflow-hidden" id="workshop-details" data-testid="workshop-details-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-violet-100 px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4" />
            Limited Availability
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
            Next <span className="gradient-text">Workshop</span>
          </h2>
          <p className="text-lg text-slate-500">
            An intimate, hands-on session designed for maximum learning and real results.
          </p>
        </motion.div>

        <motion.div {...staggerContainer} className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {details.map((item, i) =>
          <motion.div
            key={i}
            {...staggerItem}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-6 text-center border border-slate-100 shadow-lg">

              <motion.div
              className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[item.color]} flex items-center justify-center mx-auto mb-4 shadow-lg`}
              whileHover={{ rotate: 10 }}>

                <item.icon className="w-6 h-6 text-white" />
              </motion.div>
              <p className="text-sm text-slate-400 mb-1">{item.label}</p>
              <p className="text-lg font-semibold text-slate-900">{item.value}</p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// Walk Away With Section
const WalkAwaySection = () => {
  const items = [
  "A live professional website you built yourself",
  "A repeatable AI system you can use again and again",
  "The knowledge to build websites for future projects",
  "Agency-level design principles for high-converting websites",
  "A conversion-ready website structure that generates leads",
  "SEO foundations baked in so your site is discoverable from launch",
  "A WordPress-free setup with no plugins or maintenance headaches"];


  return (
    <section className="relative py-24 md:py-32 gradient-section-pink overflow-hidden" id="walk-away" data-testid="walk-away-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeInUp}>
            <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-pink-600 mb-4 bg-pink-100 px-4 py-2 rounded-full">
              <Star className="w-4 h-4" />
              Real Outcomes
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              What You'll <span className="gradient-text">Walk Away With</span>
            </h2>
            <p className="text-lg text-slate-500 mb-8">
              You'll walk away with something far more valuable than just a website:{' '}
              <span className="font-semibold text-slate-900">Independence.</span>
            </p>
            <ul className="space-y-4">
              {items.map((item, i) =>
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-3 bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-pink-100">

                  <CheckCircle2 className="w-6 h-6 text-pink-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </motion.li>
              )}
            </ul>
          </motion.div>

          <motion.div {...scaleIn} className="relative">
            <div className="aspect-square rounded-3xl bg-gradient-to-br from-violet-500 via-blue-500 to-pink-500 p-1">
              <div className="w-full h-full rounded-3xl bg-gradient-to-br from-violet-100 via-blue-50 to-pink-100 p-8 flex items-center justify-center">
                <div className="text-center">
                  <motion.div
                    className="w-28 h-28 rounded-full bg-white shadow-2xl flex items-center justify-center mx-auto mb-6"
                    animate={{ y: [0, -15, 0], rotate: [0, 5, 0, -5, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>

                    <Rocket className="w-14 h-14 text-violet-600" />
                  </motion.div>
                  <p className="text-3xl font-bold text-slate-900">Independence</p>
                  <p className="text-slate-500">From agencies forever</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

// Investment Section
const InvestmentSection = ({ onCtaClick }) =>
<section className="relative py-24 md:py-32 bg-white overflow-hidden" id="investment" data-testid="investment-section">
    {/* Background decoration */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-violet-100 rounded-full blur-3xl opacity-50" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-pink-100 rounded-full blur-3xl opacity-50" />
    </div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div
      {...scaleIn}
      className="max-w-2xl mx-auto text-center">

        <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-violet-100 px-4 py-2 rounded-full">
          <Sparkles className="w-4 h-4" />
          One-Time Payment, Lifetime Skills
        </span>
        <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-8">
          Workshop <span className="gradient-text">Investment</span>
        </h2>

        <motion.div
        className="relative bg-white rounded-3xl p-1 shadow-2xl"
        whileHover={{ scale: 1.02 }}>

          {/* Gradient border */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500 via-blue-500 to-pink-500 rounded-3xl" />
          
          <div className="relative bg-white rounded-[22px] p-10">
            <motion.div
            className="mb-6"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 2, repeat: Infinity }}>

              <span className="text-3xl md:text-4xl font-bold text-slate-400 line-through mr-3">$3,000</span>
              <span className="text-6xl md:text-7xl font-bold gradient-text">$749</span>
              <span className="text-xl text-slate-500 ml-2">AUD per person</span>
            </motion.div>

            <ul className="space-y-3 mb-8 text-left max-w-sm mx-auto">
              {[
            "4-hour hands-on workshop",
            "Step-by-step guided instruction",
            "Agency-level design principles for high-converting websites",
            "SEO-ready website structure from the ground up",
            "A live working website by the end",
            "Small group format (limited seats)"].
            map((item, i) =>
            <li key={i} className="flex items-center gap-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-violet-500 flex-shrink-0" />
                  {item}
                </li>
            )}
            </ul>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
              onClick={onCtaClick}
              className="w-full bg-gradient-to-r from-violet-600 via-blue-500 to-pink-500 hover:from-violet-700 hover:via-blue-600 hover:to-pink-600 text-white rounded-full py-6 text-lg font-medium shadow-xl shadow-violet-500/25"
              data-testid="investment-cta-button">

                Secure Your Seat Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
            <p className="text-sm text-slate-400 mt-4">Limited seats available for each session (15 seats only)</p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>;


// Comparison Section
const ComparisonSection = () => {
  const comparisons = [
  { most: "Talk about tools", this: "Teaches how professionals think about websites" },
  { most: "Show templates", this: "Shows how to control AI instead of fighting it" },
  { most: "Leave you stuck afterwards", this: "Focuses on conversion, structure, and polish" },
  { most: "Based on YouTube hacks", this: "Based on real client workflows" }];


  return (
    <section className="relative py-24 md:py-32 gradient-section-purple overflow-hidden" id="comparison" data-testid="comparison-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full">
            <Zap className="w-4 h-4" />
            The Difference
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
            Why This Workshop Is <span className="gradient-text">Different</span>
          </h2>
        </motion.div>

        <motion.div
          {...scaleIn}
          className="max-w-4xl mx-auto bg-white rounded-3xl border border-violet-100 overflow-hidden shadow-xl">

          <div className="grid grid-cols-2">
            <div className="p-6 bg-slate-50 border-b border-slate-200">
              <p className="font-semibold text-slate-400">Most Workshops</p>
            </div>
            <div className="p-6 bg-gradient-to-r from-violet-50 to-pink-50 border-b border-violet-100">
              <p className="font-semibold gradient-text">This Workshop</p>
            </div>
          </div>
          {comparisons.map((row, i) =>
          <motion.div
            key={i}
            className="grid grid-cols-2 border-b border-slate-100 last:border-0"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}>

              <div className="p-6 text-slate-500 flex items-center gap-2">
                <X className="w-5 h-5 text-slate-300 flex-shrink-0" />
                {row.most}
              </div>
              <div className="p-6 text-slate-700 bg-gradient-to-r from-violet-50/50 to-pink-50/50 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-violet-500 flex-shrink-0" />
                {row.this}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// How It Runs Section
const HowItRunsSection = () => {
  const timeline = [
  { time: "30 min", title: "Introduction", desc: "Philosophy and foundations overview", color: "violet" },
  { time: "60 min", title: "Getting Started", desc: "Setup, tools, and preparing your website", color: "blue" },
  { time: "90 min", title: "Build Phase", desc: "Hands-on AI website creation", color: "pink" },
  { time: "60 min", title: "Polish & Launch", desc: "Refinement, setup, and going live", color: "violet" }];


  const colorClasses = {
    violet: 'from-violet-500 to-purple-600',
    blue: 'from-blue-500 to-cyan-600',
    pink: 'from-pink-500 to-rose-600'
  };

  return (
    <section className="relative py-24 md:py-32 gradient-section-purple overflow-hidden" id="how-it-runs" data-testid="how-it-runs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-violet-100 px-4 py-2 rounded-full">
            <Clock className="w-4 h-4" />
            Workshop Format
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
            How the Workshop <span className="gradient-text">Runs</span>
          </h2>
          <p className="text-lg text-slate-500">
            You're learning how to build websites properly — fast.
          </p>
        </motion.div>

        <motion.div {...staggerContainer} className="grid md:grid-cols-4 gap-6">
          {timeline.map((step, i) =>
          <motion.div
            key={i}
            {...staggerItem}
            whileHover={{ y: -8 }}
            className="relative">

              <div className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-6 text-center border border-slate-100 shadow-lg hover:shadow-xl transition-all">
                <motion.span
                className={`inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r ${colorClasses[step.color]} text-white font-semibold rounded-full text-sm mb-4 shadow-lg`}
                whileHover={{ scale: 1.1 }}>

                  {step.time}
                </motion.span>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-500">{step.desc}</p>
              </div>
              {i < timeline.length - 1 &&
            <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-violet-300 to-pink-300" />
            }
            </motion.div>
          )}
        </motion.div>

        <motion.p
          {...fadeInUp}
          className="text-center text-slate-500 mt-8 bg-violet-50 rounded-2xl p-4 max-w-xl mx-auto">

          <span className="font-semibold text-violet-600">You build alongside the instructor.</span>{' '}
          This is hands-on, not watch-and-learn.
        </motion.p>
      </div>
    </section>);

};

// FAQ Section
const FAQSection = () => {
  const faqs = [
  { q: "Do I need any coding experience?", a: "No coding experience is required. If you can use a computer and navigate the web, you have all the skills needed. We'll guide you through every step." },
  { q: "What if I don't finish my website during the workshop?", a: "The workshop is designed so you'll have a complete, live website by the end. If you need additional support, the founders will personally schedule a one-on-one call with you after the workshop. We guarantee you'll walk away with your own professional website — no exceptions." },
  { q: "What tools will we be using?", a: "We use modern AI-powered tools that don't require coding. You'll receive guidance on setting up your AI builder subscription, which is all you need to follow along." },
  { q: "What if I already have a website?", a: "This workshop is perfect for you. Once you experience this system, you'll want to rebuild your current website using the methods we teach — it's that powerful. The site you create will be far more professional, bespoke, and conversion-focused than what you have now." },
  { q: "Is there ongoing support after the workshop?", a: "You'll leave with the knowledge to build independently. The workshop focuses on teaching you independence, so you won't need ongoing support. If you ever have questions, you can reach us anytime." }];


  return (
    <section className="relative py-24 md:py-32 bg-white overflow-hidden" id="faq" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-violet-600 mb-4 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-violet-200">
            <FileQuestion className="w-4 h-4" />
            Questions
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
        </motion.div>

        <motion.div {...scaleIn} className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, i) =>
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-white rounded-2xl border border-violet-100 px-6 overflow-hidden shadow-lg">

                <AccordionTrigger
                className="text-left font-semibold text-slate-900 text-lg py-5 hover:no-underline"
                data-testid={`faq-trigger-${i}`}>

                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-base leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </motion.div>
      </div>
    </section>);

};

// Final CTA Section (Dark Space Theme)
const FinalCTASection = ({ onCtaClick }) =>
<section className="relative py-32 md:py-40 space-bg overflow-hidden" id="final-cta" data-testid="final-cta-section">
    <StarsBackground count={100} />
    
    {/* Nebula effects */}
    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/30 rounded-full blur-[120px]" />
    <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-600/30 rounded-full blur-[100px]" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-600/20 rounded-full blur-[80px]" />
    
    {/* Floating rockets */}
    <motion.div
    className="absolute top-20 left-20 hidden lg:block"
    animate={{ y: [0, -30, 0], rotate: [-45, -40, -45] }}
    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>

      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center opacity-50">
        <Rocket className="w-6 h-6 text-white" />
      </div>
    </motion.div>
    <motion.div
    className="absolute bottom-20 right-20 hidden lg:block"
    animate={{ y: [0, -20, 0], rotate: [45, 50, 45] }}
    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}>

      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center opacity-50">
        <Star className="w-5 h-5 text-white" />
      </div>
    </motion.div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div
      {...scaleIn}
      className="text-center py-12">

        <motion.h2
        className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6"
        animate={{ textShadow: ['0 0 30px rgba(139,92,246,0.3)', '0 0 60px rgba(139,92,246,0.5)', '0 0 30px rgba(139,92,246,0.3)'] }}
        transition={{ duration: 3, repeat: Infinity }}>

          Ready to Build Your Website?
        </motion.h2>
        <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10">
          In just one afternoon you'll go from idea to a live website — built by you.{' '}
          <span className="text-white font-medium">No code. No WordPress. No relying on agencies.</span>
        </p>
        
        <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}>

          <Button
          onClick={onCtaClick}
          className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-12 py-7 text-xl font-medium shadow-2xl shadow-white/20 transition-all"
          data-testid="final-cta-button">

            Secure Your Seat — <span className="line-through opacity-60">$3k</span> $749
            <ArrowRight className="w-6 h-6 ml-3" />
          </Button>
        </motion.div>

        <p className="text-slate-400 mt-6 text-sm">
          Limited seats • Melbourne • 28 May 2026
        </p>
      </motion.div>
    </div>
  </section>;


// Privacy Policy Modal
const PrivacyModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[80vh] overflow-y-auto" data-testid="privacy-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Privacy Policy</DialogTitle>
          <DialogDescription className="text-slate-500">
            At Spaceship Media, we respect your privacy and are committed to protecting your personal information.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-6 text-slate-700 text-sm leading-relaxed">
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Information We Collect</h3>
            <p className="mb-2">When you register for the workshop, we may collect:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Business information (if provided)</li>
              <li>Payment details (processed securely via third-party providers)</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">How We Use Your Information</h3>
            <p className="mb-2">We use your information to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Confirm your workshop registration</li>
              <li>Communicate important updates</li>
              <li>Provide pre-workshop materials</li>
              <li>Improve our services and offerings</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Data Sharing</h3>
            <p className="mb-2">We do not sell or share your personal information with third parties, except:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>With trusted service providers (such as payment processors)</li>
              <li>When required by law</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Data Security</h3>
            <p>We take reasonable steps to protect your information from unauthorised access or misuse.</p>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Contact</h3>
            <p>If you have any questions about this policy, please contact: <a href="mailto:launch@spaceshipmedia.com.au" className="text-violet-600 hover:underline">launch@spaceshipmedia.com.au</a></p>
          </div>
        </div>
      </DialogContent>
    </Dialog>);
};

// Terms & Conditions Modal
const TermsModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[80vh] overflow-y-auto" data-testid="terms-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Terms & Conditions</DialogTitle>
          <DialogDescription className="text-slate-500">
            By registering for the workshop, you agree to the following terms.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-6 text-slate-700 text-sm leading-relaxed">
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Workshop Participation</h3>
            <p>This is a hands-on workshop. While we aim to help every participant build a live website, results may vary depending on individual progress and preparation.</p>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Payments</h3>
            <p>All payments are required to secure your seat. Prices are listed in AUD.</p>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Refund Policy</h3>
            <p>Due to limited seats and preparation involved, registrations are non-refundable. However, you may transfer your spot to another person with prior notice.</p>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Changes to Workshop</h3>
            <p>We reserve the right to reschedule or make changes to the workshop format if required. Participants will be notified in advance.</p>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Responsibility</h3>
            <p>Participants are responsible for bringing required materials such as a laptop and any relevant assets.</p>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Intellectual Property</h3>
            <p>All materials, frameworks, and processes shared during the workshop remain the intellectual property of Spaceship Media and are for personal use only.</p>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-base mb-2">Limitation of Liability</h3>
            <p>Spaceship Media is not liable for any business outcomes resulting from the use of knowledge gained during the workshop.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>);
};

// Footer
const Footer = ({ onPrivacyClick, onTermsClick, onContactClick }) =>
<footer className="relative bg-slate-950 text-white py-12 border-t border-violet-900/50 overflow-hidden" data-testid="footer">
    <div className="absolute inset-0 opacity-30">
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-violet-900/20 via-transparent to-pink-900/20" />
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <motion.img
        src={Logo}
        alt="Spaceship Media"
        className="h-10 brightness-0 invert"
        whileHover={{ scale: 1.05 }} />

        <p className="text-slate-400 text-sm">
          © {new Date().getFullYear()} Spaceship Media. All rights reserved.
        </p>
        <div className="flex gap-6">
          <button onClick={onPrivacyClick} className="text-slate-400 hover:text-violet-400 transition-colors text-sm" data-testid="footer-privacy-link">Privacy</button>
          <button onClick={onTermsClick} className="text-slate-400 hover:text-violet-400 transition-colors text-sm" data-testid="footer-terms-link">Terms</button>
          <button onClick={onContactClick} className="text-slate-400 hover:text-violet-400 transition-colors text-sm" data-testid="footer-contact-link">Contact</button>
        </div>
      </div>
    </div>
  </footer>;


// Mobile Sticky CTA
const MobileStickyCTA = ({ onCtaClick }) =>
<div className="sticky-cta md:hidden" data-testid="mobile-sticky-cta">
    <Button
    onClick={onCtaClick}
    className="w-full bg-gradient-to-r from-violet-600 via-blue-500 to-pink-500 hover:from-violet-700 hover:via-blue-600 hover:to-pink-600 text-white rounded-full py-4 text-sm font-medium shadow-xl shadow-violet-500/25">
      <span className="line-through opacity-60">$3k</span> $749 — Secure Your Seat
    </Button>
  </div>;


// Main App
function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#privacy') setIsPrivacyModalOpen(true);
    if (hash === '#terms') setIsTermsModalOpen(true);
  }, []);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);

  const openPrivacy = () => { window.location.hash = 'privacy'; setIsPrivacyModalOpen(true); };
  const closePrivacy = () => { window.location.hash = ''; setIsPrivacyModalOpen(false); };

  const openTerms = () => { window.location.hash = 'terms'; setIsTermsModalOpen(true); };
  const closeTerms = () => { window.location.hash = ''; setIsTermsModalOpen(false); };

  return (
    <div className="App">
      <Toaster position="top-center" />
      <Navigation onCtaClick={openModal} />
      
      <main>
        <HeroSection onCtaClick={openModal} />
        <ProblemSection />
        <PhilosophySection />
        <OutcomeSection />
        <SocialProofSection />
        <AuthorityStrip />
        <WhoItsForSection onContactClick={openContactModal} />
        <MarqueeBanner />
        <HowItRunsSection />
        <WhatToBringSection />
        <WorkshopDetailsSection />
        <WalkAwaySection />
        <InvestmentSection onCtaClick={openModal} />
        <ComparisonSection />
        <FAQSection />
        <FinalCTASection onCtaClick={openModal} />
      </main>

      <Footer
        onPrivacyClick={openPrivacy}
        onTermsClick={openTerms}
        onContactClick={openContactModal}
      />
      <MobileStickyCTA onCtaClick={openModal} />
      <FormModal isOpen={isModalOpen} onClose={closeModal} />
      <ContactModal isOpen={isContactModalOpen} onClose={closeContactModal} />
      <PrivacyModal isOpen={isPrivacyModalOpen} onClose={closePrivacy} />
      <TermsModal isOpen={isTermsModalOpen} onClose={closeTerms} />
    </div>);

}

export default App;